import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { createInventorySkuMapping, fetchCatalogSkus } from "../../slices/inventorySkuMappingSlice";

const MARKETPLACE_SUFFIX_RE = /-(FBA|FBM)$/i;

function normalizeSupplyLookupQuery(value) {
    const normalized = String(value || "").trim();
    if (!normalized) return "";
    return normalized.replace(MARKETPLACE_SUFFIX_RE, "");
}

function phasedStatusBadgeClass(status) {
    if (status === "ACTIVE") return "bg-green-100 text-green-800";
    if (status === "SUNSET") return "bg-amber-100 text-amber-900";
    if (status === "PLANNED") return "bg-blue-100 text-blue-800";
    if (status === "IN_ACTIVE") return "bg-gray-100 text-gray-700";
    return "bg-gray-100 text-gray-700";
}

function phasedStatusLabel(status) {
    if (status === "ACTIVE") return "Active";
    if (status === "SUNSET") return "Sunset";
    if (status === "IN_ACTIVE") return "Inactive";
    if (status === "PLANNED") return "Planned";
    return String(status || "");
}

function edenStatusLabel(edenStatus) {
    return String(edenStatus || "").toUpperCase() === "DELETED" ? "Deleted" : "Active";
}

function edenStatusBadgeClass(edenStatus) {
    return String(edenStatus || "").toUpperCase() === "DELETED"
        ? "bg-red-100 text-red-800"
        : "bg-emerald-100 text-emerald-800";
}

function CatalogSkuOptionMeta({ edenStatus, phasedStatus, showEdenStatus }) {
    const phaseLabel = phasedStatus ? phasedStatusLabel(phasedStatus) : null;
    const edenLabel = edenStatus ? edenStatusLabel(edenStatus) : null;

    if (!showEdenStatus && !phaseLabel) return null;

    return (
        <div className="flex shrink-0 flex-col items-end gap-0.5 text-[10px] leading-tight">
            {showEdenStatus && edenLabel ? (
                <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${edenStatusBadgeClass(edenStatus)}`}
                >
                    Eden Status · {edenLabel}
                </span>
            ) : null}
            {phaseLabel ? (
                <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${phasedStatusBadgeClass(phasedStatus)}`}
                >
                    Product Status · {phaseLabel}
                </span>
            ) : null}
        </div>
    );
}

function ProductSkuSearchField({
    idPrefix,
    label,
    required,
    placeholder,
    picker,
    selected,
    onSelect,
    excludeProductId,
    skipSearchIcon = false,
    showEdenStatus = false,
    loadCatalogSkus,
}) {
    const [query, setQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const rootRef = useRef(null);
    const selectedId = selected?.id ?? null;
    const selectedSku = selected?.sku ?? "";

    useEffect(() => {
        if (selectedId) setQuery(selectedSku);
    }, [selectedId, selectedSku]);

    useEffect(() => {
        const delay = 300;
        const t = setTimeout(
            () => setDebounced(picker === "supply" ? normalizeSupplyLookupQuery(query) : query.trim()),
            delay
        );
        return () => clearTimeout(t);
    }, [picker, query]);

    useEffect(() => {
        if (!open) return;

        if (debounced.length < 1) {
            setItems([]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        loadCatalogSkus({
            picker,
            includeNameMatch: true,
            limit: 50,
            ...(excludeProductId ? { excludeProductId } : {}),
            ...(debounced ? { search: debounced } : {}),
        })
            .then((list) => {
                if (!cancelled) setItems(list || []);
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [debounced, open, picker, excludeProductId, loadCatalogSkus]);

    useEffect(() => {
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const handleChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        if (selected && v !== selected.sku) onSelect(null);
        setOpen(true);
    };

    const pick = useCallback(
        (row) => {
            onSelect(row);
            setQuery(row.sku || "");
            setOpen(false);
        },
        [onSelect]
    );

    const clearQuery = useCallback(() => {
        setQuery("");
        onSelect(null);
        setOpen(true);
    }, [onSelect]);

    return (
        <div ref={rootRef} className="flex flex-col gap-1.5">
            <label htmlFor={`${idPrefix}-input`} className="text-xs font-medium text-gray-900">
                {label}
                {required ? <span className="text-red-600"> *</span> : null}
            </label>
            <div className="relative">
                {!skipSearchIcon && (
                    <MagnifyingGlassIcon
                        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        aria-hidden
                    />
                )}
                <input
                    id={`${idPrefix}-input`}
                    type="search"
                    autoComplete="off"
                    value={query}
                    onChange={handleChange}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    className={`no-search-input-chrome border-input h-8 w-full min-w-0 rounded-md border bg-transparent py-1 text-sm shadow-sm outline-none transition-all duration-200 focus:ring-2 focus:ring-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none ocus:outline-none ${skipSearchIcon ? "pl-3" : "pl-8"} ${query ? "pr-8" : "pr-3"}`}
                />
                {query ? (
                    <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                        onClick={clearQuery}
                        aria-label="Clear search"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                ) : null}
                {open && (
                    <div
                        className="absolute left-0 right-0 top-full z-[120] mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm"
                        role="listbox"
                    >
                        {loading ? (
                            <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
                        ) : items.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                {debounced.length < 1 ? "Type to search…" : "No matches."}
                            </div>
                        ) : (
                            items.map((row) => (
                                <button
                                    key={row.id}
                                    type="button"
                                    role="option"
                                    aria-selected={selectedId === row.id}
                                    className="flex w-full items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => pick(row)}
                                >
                                    <span className="min-w-0 flex-1 truncate font-semibold uppercase text-gray-900">
                                        {row.sku}
                                    </span>
                                    <CatalogSkuOptionMeta
                                        edenStatus={row.eden_status}
                                        phasedStatus={row.status}
                                        showEdenStatus={showEdenStatus}
                                    />
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

const AddRowModal = ({ isOpen, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const loadCatalogSkus = useCallback(
        (params) =>
            dispatch(fetchCatalogSkus({ ...params, includeEdenDeleted: true })).unwrap(),
        [dispatch]
    );

    const [formKey, setFormKey] = useState(0);
    const [channel, setChannel] = useState("SHOPIFY");
    const [executionProduct, setExecutionProduct] = useState(null);
    const [supplyProduct, setSupplyProduct] = useState(null);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setFormKey((k) => k + 1);
        setChannel("SHOPIFY");
        setExecutionProduct(null);
        setSupplyProduct(null);
        setNotes("");
        setSubmitting(false);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!executionProduct?.id || !supplyProduct?.id) return;
        if (executionProduct.id === supplyProduct.id) setSupplyProduct(null);
    }, [executionProduct?.id, supplyProduct?.id]);

    const barcodeDisplay =
        executionProduct?.barcode && String(executionProduct.barcode).trim() !== "" && executionProduct.barcode !== "-"
            ? executionProduct.barcode
            : "";

    const canSubmit =
        Boolean(executionProduct?.id) && !submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await dispatch(
                createInventorySkuMapping({
                    execution_product_id: executionProduct.id,
                    channel,
                    ...(supplyProduct?.id ? { supply_product_id: supplyProduct.id } : {}),
                    notes: notes.trim() || null,
                })
            ).unwrap();
            toast.success("Mapping row added.");
            onClose();
            onSuccess?.();
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Could not add mapping.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4"
            aria-modal="true"
            role="dialog"
            aria-labelledby="add-manual-mapping-title"
        >
            <button
                type="button"
                className="absolute inset-0 bg-gray-900/40"
                aria-label="Close"
                onClick={onClose}
            />
            <div className="relative z-[121] flex max-h-[90vh] w-full max-w-[512px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
                <div className="flex items-start justify-between gap-3 p-6 pb-0">
                    <h2 id="add-manual-mapping-title" className="text-sm font-semibold text-primary">
                        Add Manual Mapping Row
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col overflow-y-auto px-6 py-4 pb-6">
                    <div className="flex flex-col gap-4">
                        <ProductSkuSearchField
                            key={`ex-${formKey}`}
                            idPrefix="execution-sku"
                            label="Execution SKU"
                            required
                            skipSearchIcon={true}
                            placeholder="e.g. OLD-SKU-BLK"
                            picker="execution"
                            selected={executionProduct}
                            onSelect={setExecutionProduct}
                            showEdenStatus
                            loadCatalogSkus={loadCatalogSkus}
                        />

                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-gray-900">Channel</span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setChannel("SHOPIFY")}
                                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                                        channel === "SHOPIFY"
                                            ? "border-gray-900 bg-gray-900 text-white"
                                            : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                    }`}
                                >
                                    Shopify
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setChannel("AMAZON")}
                                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                                        channel === "AMAZON"
                                            ? "border-gray-900 bg-gray-900 text-white"
                                            : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                    }`}
                                >
                                    Amazon
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="barcode-display" className="text-xs font-medium text-gray-900">
                                Barcode
                            </label>
                            <input
                                id="barcode-display"
                                type="text"
                                readOnly
                                value={barcodeDisplay}
                                placeholder="Filled when you select an execution product"
                                className="w-full h-8 shadow-sm cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 py-2 px-3 text-xs text-gray-700 placeholder:text-gray-40 ocus:outline-none focus:ring-2 focus:ring-gray-9000"
                            />
                        </div>

                        <ProductSkuSearchField
                            key={`sup-${formKey}`}
                            idPrefix="supply-sku"
                            label="Supply SKU"
                            placeholder="Search supply SKU or product name…"
                            picker="supply"
                            selected={supplyProduct}
                            onSelect={setSupplyProduct}
                            excludeProductId={executionProduct?.id}
                            showEdenStatus
                            loadCatalogSkus={loadCatalogSkus}
                        />

                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="mapping-notes" className="text-xs font-medium text-gray-900">
                                Notes
                            </label>
                            <input
                                id="mapping-notes"
                                type="text"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="e.g. Renamed in 2022 rebrand"
                                className="w-full rounded-md shadow-sm h-8 border border-gray-300 bg-white py-1 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-2 pt-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md border border-gray-300 bg-white h-8 rounded-md px-3 text-sm font-medium text-gray-900 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            className="rounded-md bg-gray-900 h-8 rounded-md px-3 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                        >
                            {submitting ? "Adding…" : "Add Row"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddRowModal;
